import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, Trash2, Users, Star, AlertTriangle, FileSpreadsheet, PhoneOff } from 'lucide-react';
import { customersApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';

/**
 * Numbers held by more than one contact entry.
 *
 * The contact directory is one entry per (person, customer), so one man who
 * owns eleven firms is eleven entries on the same number — and eleven lines in
 * a phone book fed from it. Some of those are real and some are leftovers, and
 * only the person reading the list knows which, so nothing is merged here:
 * every copy is shown and the redundant ones are ticked off by hand.
 *
 * "Same name" marks the groups where the number AND the person's name repeat.
 * Those are the ones that are nearly always worth clearing.
 */

interface Entry {
  mapping_id: number;
  contact_id: number;
  contact_person: string | null;
  mobile_no: string | null;
  primary: boolean;
  customer_id: number;
  customer_name: string | null;
  customer_active: boolean;
  created_at: string | null;
}

interface Group {
  number: string;
  copies: number;
  customers: number;
  same_name: boolean;
  entries: Entry[];
}

interface BadNumber {
  mapping_id: number;
  contact_person: string | null;
  mobile_no: string | null;
  digits: number;
  has_letters: number;
  customer_id: number;
  customer_name: string | null;
  customer_mobile: string | null;
  created_at: string | null;
}

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  const d = new Date(String(s).replace(' ', 'T'));
  return isNaN(d.getTime()) ? String(s) : d.toLocaleDateString('en-GB');
};

const DuplicateContacts: React.FC = () => {
  const { showSuccess, showError } = useToast();

  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [search, setSearch] = useState('');
  const [sameNameOnly, setSameNameOnly] = useState(false);
  const [minCopies, setMinCopies] = useState(2);
  const [picked, setPicked] = useState<Record<number, boolean>>({});
  const [tab, setTab] = useState<'dupes' | 'bad'>('dupes');
  const [bad, setBad] = useState<BadNumber[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'bad') {
        const res = await customersApi.getInvalidContacts(search.trim() || undefined);
        setBad(res.data || []);
        setPicked({});
        return;
      }
      const res = await customersApi.getDuplicateContacts({
        search: search.trim() || undefined,
        sameNameOnly,
        minCopies,
      });
      setGroups(res.data || []);
      setPicked({});
    } catch (e: any) {
      showError('Could not load', e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sameNameOnly, minCopies, tab]);

  useEffect(() => { load(); }, [load]);

  const pickedIds = useMemo(
    () => Object.entries(picked).filter(([, v]) => v).map(([k]) => Number(k)),
    [picked],
  );

  const totals = useMemo(() => ({
    numbers: groups.length,
    entries: groups.reduce((t, g) => t + g.entries.length, 0),
    removable: groups.reduce((t, g) => t + Math.max(0, g.entries.length - 1), 0),
  }), [groups]);

  /** Tick every copy except the one worth keeping: the primary if there is one,
   *  otherwise the oldest entry, which is the one other records point at. */
  const pickRedundant = (only?: Group) => {
    const next: Record<number, boolean> = only ? { ...picked } : {};
    for (const g of (only ? [only] : groups)) {
      const keep = g.entries.find(e => e.primary) || g.entries[0];
      for (const e of g.entries) next[e.mapping_id] = e.mapping_id !== keep.mapping_id;
    }
    setPicked(next);
  };

  const remove = async () => {
    if (!pickedIds.length) return;
    const affected = groups.filter(g => g.entries.some(e => picked[e.mapping_id]));
    const emptied = affected.filter(g => g.entries.every(e => picked[e.mapping_id]));
    const warn = [
      `Remove ${pickedIds.length} contact entr${pickedIds.length === 1 ? 'y' : 'ies'} from the directory?`,
      emptied.length
        ? `${emptied.length} number(s) would have no entry left at all — nobody would find the customer by that number.`
        : '',
      'Each entry is marked inactive, so it can be put back from the customer\'s contact list.',
    ].filter(Boolean).join('\n\n');
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(warn)) return;

    setRemoving(true);
    try {
      const res = await customersApi.removeContacts(pickedIds);
      showSuccess('Removed', `${res.removed} contact entr${res.removed === 1 ? 'y' : 'ies'} taken out of the directory`);
      load();
    } catch (e: any) {
      showError('Could not remove', e?.message || 'Unknown error');
    } finally {
      setRemoving(false);
    }
  };

  const exportExcel = async () => {
    if (!groups.length) { showError('Export', 'Nothing to export'); return; }
    const { utils, writeFile } = await import('xlsx');
    const rows: any[] = [];
    groups.forEach(g => g.entries.forEach((e, i) => rows.push({
      'Mobile': g.number,
      'Copies': i === 0 ? g.copies : '',
      'Same Name': i === 0 ? (g.same_name ? 'Yes' : 'No') : '',
      'Contact Person': e.contact_person || '',
      'Customer': e.customer_name || '',
      'Customer Active': e.customer_active ? 'Yes' : 'No',
      'Primary': e.primary ? 'Yes' : '',
      'Added On': fmtDate(e.created_at),
      'Entry Id': e.mapping_id,
      'Keep / Remove': '',
    })));
    const ws = utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 13 }, { wch: 7 }, { wch: 10 }, { wch: 24 }, { wch: 32 }, { wch: 14 }, { wch: 8 }, { wch: 11 }, { wch: 9 }, { wch: 15 }];
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Duplicate Contacts');
    writeFile(wb, `Duplicate_Contacts_${new Date().toISOString().slice(0, 10)}.xlsx`);
    showSuccess('Exported', `${groups.length} number(s), ${rows.length} entries`);
  };

  /** The numbers whose every entry is ticked — the ones that will disappear
   *  from the directory entirely, and so the ones to delete in Google too. */
  const exportNumbersToDelete = () => {
    const gone = groups.filter(g => g.entries.length > 0 && g.entries.every(e => picked[e.mapping_id]));
    if (!gone.length) { showError('Nothing to list', 'No number is fully ticked off'); return; }
    const text = gone.map(g => g.number).join('\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `Numbers_to_delete_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
    showSuccess('Saved', `${gone.length} number(s) listed`);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="bg-white rounded-lg shadow p-4 max-w-[1100px] mx-auto">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Users className="h-5 w-5 text-blue-600" />
            <div>
              <h1 className="text-lg font-semibold text-gray-800 leading-tight">Duplicate Contact Numbers</h1>
              <p className="text-[11px] text-gray-500">
                {loading
                  ? 'Loading…'
                  : `${totals.numbers} number(s), ${totals.entries} entries, ${totals.removable} could go`}
                {/* The server caps the list; without saying so, working through
                    it would look finished while more remain. */}
                {!loading && totals.numbers >= 500 && ' — first 500 shown, narrow with search or copies'}
              </p>
            </div>
          </div>
          <button onClick={exportExcel} className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
          </button>
          <button onClick={load} disabled={loading} className="p-2 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex gap-1 mb-3">
          {([['dupes', 'Duplicates'], ['bad', 'Bad numbers']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => { setTab(key); setPicked({}); }}
              className={`px-3 py-1.5 text-xs font-bold rounded border ${
                tab === key ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Number, person or company…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          {tab === 'dupes' && (<>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
            <input type="checkbox" checked={sameNameOnly} onChange={e => setSameNameOnly(e.target.checked)} />
            Same name only
          </label>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
            At least
            <select
              value={minCopies}
              onChange={e => setMinCopies(Number(e.target.value))}
              className="border border-gray-300 rounded px-1.5 py-1 text-xs"
            >
              {[2, 3, 4, 5, 8].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            copies
          </label>
          <button onClick={() => pickRedundant()} className="px-3 py-1.5 text-xs font-bold rounded border border-amber-300 text-amber-700 hover:bg-amber-50">
            Tick all but one
          </button>
          </>)}
        </div>

        {/* A number on several customers is often one owner with several firms,
            which is not a mistake. Said once, here, rather than per row. */}
        <div className="flex gap-2 p-2.5 mb-3 rounded border border-blue-200 bg-blue-50 text-[11px] text-blue-800">
          {tab === 'bad' ? <PhoneOff className="h-4 w-4 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />}
          {tab === 'bad' ? (
            <span>
              These numbers are not 10 digits, so they can never match an incoming call and cannot be
              dialled. Most were pasted with a <strong>+91</strong>, a leading zero or spaces back when
              the column could hold only ten characters, so the real digits fell off the end — the lost
              part cannot be worked out, so correct them on the customer or remove them. They are no
              longer sent to the phone.
            </span>
          ) : (
          <span>
            The same number against different companies is usually one person who owns several
            firms — removing those loses a real link. The rows worth clearing are repeats of the
            same person, marked <strong>same name</strong>. Removing marks an entry inactive; it can
            be restored from the customer's contact list.
          </span>
          )}
        </div>

        {tab === 'bad' ? (
          loading ? (
            <div className="py-12 text-center text-gray-400">Loading…</div>
          ) : bad.length === 0 ? (
            <div className="py-12 text-center text-gray-400">Every contact number is a clean 10-digit mobile.</div>
          ) : (
            <div className="border border-gray-200 rounded overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="px-3 py-1.5 w-8"></th>
                    <th className="px-2 py-1.5 text-left font-bold">Stored number</th>
                    <th className="px-2 py-1.5 text-left font-bold">Digits</th>
                    <th className="px-2 py-1.5 text-left font-bold">Person</th>
                    <th className="px-2 py-1.5 text-left font-bold">Customer</th>
                    <th className="px-2 py-1.5 text-left font-bold">Customer's own number</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {bad.map(b => (
                    <tr key={b.mapping_id} className={picked[b.mapping_id] ? 'bg-red-50' : ''}>
                      <td className="px-3 py-1.5">
                        <input
                          type="checkbox"
                          checked={!!picked[b.mapping_id]}
                          onChange={ev => setPicked(p => ({ ...p, [b.mapping_id]: ev.target.checked }))}
                        />
                      </td>
                      <td className="px-2 py-1.5 font-mono font-bold text-red-700">{b.mobile_no || '(blank)'}</td>
                      <td className="px-2 py-1.5 text-gray-500">
                        {b.digits}
                        {b.has_letters ? <span className="ml-1 text-[10px] font-bold text-amber-700">not just digits</span> : null}
                      </td>
                      <td className="px-2 py-1.5 text-gray-800">{b.contact_person || '—'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{b.customer_name || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-gray-500">{b.customer_mobile || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : loading ? (
          <div className="py-12 text-center text-gray-400">Loading…</div>
        ) : groups.length === 0 ? (
          <div className="py-12 text-center text-gray-400">No number is held by more than one contact entry.</div>
        ) : (
          <div className="space-y-2">
            {groups.map(g => (
              <div key={g.number} className="border border-gray-200 rounded overflow-hidden">
                <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-200">
                  <span className="font-mono font-bold text-gray-900">{g.number}</span>
                  <span className="text-[11px] text-gray-500">{g.copies} entries · {g.customers} customer(s)</span>
                  {g.same_name && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase">Same name</span>
                  )}
                  <button
                    onClick={() => pickRedundant(g)}
                    className="ml-auto text-[11px] font-bold text-blue-600 hover:underline"
                  >
                    Tick all but one
                  </button>
                </div>
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-gray-100">
                    {g.entries.map(e => (
                      <tr key={e.mapping_id} className={picked[e.mapping_id] ? 'bg-red-50' : ''}>
                        <td className="px-3 py-1.5 w-8">
                          <input
                            type="checkbox"
                            checked={!!picked[e.mapping_id]}
                            onChange={ev => setPicked(p => ({ ...p, [e.mapping_id]: ev.target.checked }))}
                          />
                        </td>
                        <td className="px-2 py-1.5 font-semibold text-gray-800">
                          {e.contact_person || '—'}
                          {e.primary && <Star className="inline h-3 w-3 text-amber-500 ml-1" />}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600">
                          {e.customer_name || '—'}
                          {!e.customer_active && <span className="ml-1 text-[10px] text-red-500 font-bold">(inactive customer)</span>}
                        </td>
                        <td className="px-2 py-1.5 text-gray-400 text-right whitespace-nowrap">{fmtDate(e.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {pickedIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg px-4 py-3 flex flex-wrap items-center gap-3 z-50">
          <span className="text-sm font-bold text-gray-800">{pickedIds.length} selected</span>
          <button onClick={() => setPicked({})} className="text-xs font-bold text-gray-500 hover:text-gray-700">Clear</button>
          <button
            onClick={exportNumbersToDelete}
            className="ml-auto px-3 py-1.5 text-xs font-bold rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            title="Numbers that will have no entry left — the list to delete in Google Contacts"
          >
            Numbers for Google
          </button>
          <button
            onClick={remove}
            disabled={removing}
            className="flex items-center gap-1.5 px-4 py-2 rounded bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            {removing ? 'Removing…' : 'Remove selected'}
          </button>
        </div>
      )}
    </div>
  );
};

export default DuplicateContacts;
